<template>
  <div class="c_text_content_cards_standalone" :class="`c_text_content_cards_standalone--${data.textAlign}`">
    <div class="c_wrapper_2022">
      <span v-if="data.eyebrow" class="c_text_content_cards_standalone--eyebrow">
        {{ data.eyebrow }}
      </span>
      <h3 class="c_header c_text--graphik_regular c_2022--h3_graphik" v-if="data.header" v-html="data.header"></h3>
      <div
        class="c_text_content_cards_standalone--description c_2022--body"
        v-if="data.description"
        v-html="data.description"
      ></div>
      <div class="c_text_content_cards_standalone__cards" :class="cardGridClass" v-if="data.cards?.length">
        <div
          class="c_text_content_cards_standalone__cards__card"
          :class="[
            { 'c_text_content_cards_standalone__cards__card--rounded': data.roundedCorners },
            `c_text_content_cards_standalone__cards__card--${card.textAlign}`,
            { 'c_text_content_cards_standalone__cards__card--imageAsBackground': imageAsBackground },
          ]"
          v-for="(card, index) in data.cards"
          :key="index"
          :data-card-theme="data.cardTheme ?? 'lightMode'"
        >
          <div
            class="c_text_content_cards_standalone__cards__card__image"
            :class="imageDisplayClass"
            v-if="card.image?.length"
          >
            <MediaImage
              :sources="isCropped ? [card.image?.[0]?.sources] : [card.image?.[0]?.uncroppedSources]"
              :alt="card.image?.[0]?.title"
              :background="data.imageDisplay !== 'auto'"
            />
          </div>
          <div class="c_text_content_cards_standalone__cards__card__container">
            <span v-if="card.eyebrow" class="c_text_content_cards_standalone__cards__card__eyebrow">
              {{ card.eyebrow }}
            </span>
            <h4 v-if="card.title" class="c_text_content_cards_standalone__cards__card__title">
              {{ card.title }}
            </h4>
            <div
              v-if="card.description"
              v-html="card.description"
              class="c_2022--body c_text_content_cards_standalone__cards__card__description"
            ></div>
            <div
              class="c_text_content_cards_standalone__cards__card__links"
              v-if="card.links"
              v-html="card.links"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { defineComponent, computed } from '@nuxtjs/composition-api';
import MediaImage from 'Components/image/MediaImage.vue';

export default defineComponent({
  name: 'TextContentCardsStandalone',
  components: {
    MediaImage,
  },
  props: {
    data: { type: Object, default: () => {} },
  },
  setup(props) {
    // Assign class to handle grid layout
    const cardGridClass = computed(() => {
      const numberOfCards = props.data?.cards?.length ?? 0;
      
      // Don't assign a class if there are no cards
      if (numberOfCards === 0) return null;
      
      if (numberOfCards > 3) {
        return `c_text_content_cards_standalone__cards--columns-3`;
      } else {
        return `c_text_content_cards_standalone__cards--columns-${numberOfCards}`;
      }
    });

    const imageDisplayClass = `c_text_content_cards_standalone__cards__card__image--${props.data.imageDisplay}`;

    const isCropped = computed(() => {
      if (props.data.imageDisplay === 'auto') return false;
      return props.data.imageVariant === 'cropped';
    });

    const imageAsBackground = props.data.imageDisplay === 'imageAsBackground';

    return { cardGridClass, imageDisplayClass, isCropped, imageAsBackground };
  },
});
</script>

<style lang="scss">
.c_text_content_cards_standalone {
  // Component theming vars:
  --standalone-card-bg: var(--color-white);
  --standalone-card-border: var(--color-gray_221);
  --standalone-card-eyebrow: var(--color-gray_86);
  --standalone-card-color: var(--color-black);
  --standalone-card-link-color: var(--color-red);
  --standalone-card-link-hover-color: var(--color-red_179);

  --standalone-card-primary-btn-bg: var(--color-red);
  --standalone-card-primary-btn-bg--hover: var(--color-red_179);
  --standalone-card-primary-btn-color: var(--color-white);
  --standalone-card-primary-btn-color--hover: var(--color-white);
  --standalone-card-primary-btn-border: var(--color-red);
  --standalone-card-primary-btn-border--hover: var(--color-red_179);

  --standalone-card-secondary-btn-bg: transparent;
  --standalone-card-secondary-btn-bg--hover: var(--color-red);
  --standalone-card-secondary-btn-color: var(--color-black);
  --standalone-card-secondary-btn-color--hover: var(--color-white);
  --standalone-card-secondary-btn-border: var(--color-red);
  --standalone-card-secondary-btn-border--hover: var(--color-red);

  --standalone-card-image-bg-overlay: rgba(255, 255, 255, 0.8);

  padding: px_to_rem(40) 0;
  @include mq($mq_sm_to_md) {
    padding: px_to_rem(80) 0;
  }
  &--left {
    text-align: left;
  }
  &--center {
    text-align: center;
  }
  &--right {
    text-align: right;
  }
  h3 {
    @include font('graphik_semibold');
    @include fs_lh(32, 40, 24, 32);
  }
  &--eyebrow {
    display: block;
    margin-bottom: px_to_rem(8);
    @include font('graphik_semibold');
    @include fs_lh(16, 24, 16, 24);
  }
  &--description {
    margin-top: px_to_rem(16);
    @include fs_lh(16, 24, 16, 24);
  }
  &__cards {
    display: grid;
    margin-top: px_to_rem(40);
    flex-direction: column;
    gap: 5vw;
    @include mq($mq_sm_to_md) {
      flex-direction: row;
      gap: px_to_rem(32);
    }
    &--columns-1 {
      grid-template-columns: 1fr; // same for all breakpoints
      @include mq($mq_sm_to_md) {
        grid-template-columns: 1fr;
      }
      @include mq($mq_md_to_lg) {
        grid-template-columns: 1fr;
      }
    }
    &--columns-2 {
      grid-template-columns: 1fr; // mobile
      @include mq($mq_sm_to_md) {
        grid-template-columns: repeat(2, 1fr);
      }
      @include mq($mq_md_to_lg) {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    &--columns-3 {
      grid-template-columns: 1fr; // mobile
      @include mq($mq_sm_to_md) {
        grid-template-columns: repeat(2, 1fr);
      }
      @include mq($mq_md_to_lg) {
        grid-template-columns: repeat(3, 1fr);
      }
    }
    &__card {
      position: relative;
      min-width: 0;
      background-color: var(--standalone-card-bg);
      overflow: hidden;
      border: 1px solid var(--standalone-card-border);
      color: var(--standalone-card-color);
      &[data-card-theme='darkMode'] {
        --standalone-card-bg: rgb(28, 28, 28);
        --standalone-card-border: rgb(57, 57, 57);
        --standalone-card-eyebrow: var(--color-gray_178);
        --standalone-card-color: var(--color-white);
        --standalone-card-link-color: var(--color-yellow);
        --standalone-card-link-hover-color: var(--color-yellow-02);

        --standalone-card-primary-btn-bg: var(--color-yellow);
        --standalone-card-primary-btn-bg--hover: var(--color-yellow-02);
        --standalone-card-primary-btn-color: var(--color-black);
        --standalone-card-primary-btn-color--hover: var(--color-black);
        --standalone-card-primary-btn-border: var(--color-yellow);
        --standalone-card-primary-btn-border--hover: var(--color-yellow-02);

        --standalone-card-secondary-btn-bg: transparent;
        --standalone-card-secondary-btn-bg--hover: var(--color-yellow);
        --standalone-card-secondary-btn-color: var(--color-yellow);
        --standalone-card-secondary-btn-color--hover: var(--color-black);
        --standalone-card-secondary-btn-border: var(--color-yellow);
        --standalone-card-secondary-btn-border--hover: var(--color-yellow);

        --standalone-card-image-bg-overlay: rgba(0, 0, 0, 0.8);
      }
      &--imageAsBackground {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      &--rounded {
        border-radius: px_to_rem(7);
      }
      &--left {
        text-align: left;
      }
      &--center {
        text-align: center;
      }
      &__container {
        padding: 5vw;
        position: relative;
        z-index: 15;
        @include mq($mq_sm_to_md) {
          padding: px_to_rem(32);
        }
      }
      &__image {
        &--16-9 {
          aspect-ratio: 16/9;
        }
        &--4-3 {
          aspect-ratio: 4/3;
        }
        &--1-1 {
          aspect-ratio: 1/1;
        }
        &--3-4 {
          aspect-ratio: 3/4;
        }
        &--fit {
          aspect-ratio: auto;
        }
        &--imageAsBackground {
          position: absolute;
          z-index: 5;
          height: 100%;
          width: 100%;
          inset: 0;
          &:after {
            content: '';
            position: absolute;
            z-index: 10;
            width: 100%;
            height: 100%;
            inset: 0;
            background-color: var(--standalone-card-image-bg-overlay);
          }
        }
      }
      &__eyebrow {
        display: block;
        margin-bottom: px_to_rem(16);
        @include font('graphik_regular');
        @include fs_lh(12, 18, 12, 18);
        color: var(--standalone-card-eyebrow);
      }
      &__title {
        @include font('graphik_semibold');
        @include fs_lh(24, 32, 18, 28);
      }
      &__description {
        margin-top: px_to_rem(16);
        @include fs_lh(16, 24, 16, 24);
      }
      &__links {
        p {
          padding: 0;
          margin-bottom: px_to_rem(12);
        }
        a,
        a:visited,
        a:active {
          @include font('graphik_semibold');
          text-decoration: none;
          color: var(--standalone-card-link-color);
        }
        a:hover {
          text-decoration: underline;
          color: var(--standalone-card-link-hover-color);
        }
        a.c_button_2022--primary:hover,
        a.c_button_2022--secondary:hover {
          text-decoration: none;
        }
        a.c_button_2022--primary {
          background-color: var(--standalone-card-primary-btn-bg);
          border-color: var(--standalone-card-primary-btn-border);
          color: var(--standalone-card-primary-btn-color);
          &:hover {
            background-color: var(--standalone-card-primary-btn-bg--hover);
            border-color: var(--standalone-card-primary-btn-border--hover);
            color: var(--standalone-card-primary-btn-color--hover);
          }
        }
        a.c_button_2022--secondary {
          background-color: var(--standalone-card-secondary-btn-bg);
          border: 2px solid var(--standalone-card-secondary-btn-border);
          color: var(--standalone-card-secondary-btn-color);
          &:hover {
            background-color: var(--standalone-card-secondary-btn-bg--hover);
            border-color: var(--standalone-card-secondary-btn-border--hover);
            color: var(--standalone-card-secondary-btn-color--hover);
          }
        }
      }
    }
  }
}
</style>
